import React from 'react'
import styled from 'styled-components'

import { routes } from '@sketch/modules-common'

// Components
import { Button, LinkButton, Pill } from '@sketch/components'

// Local Components
import BillingInfoTable from '../BillingInfoTable'

// Utils
import { formatPriceShort, dateFormat } from '@sketch/utils'
import { PLAN_TYPE_LABEL, nextPricingDate } from 'modules/workspace/utils'

// GQL
import { BillingPlanFragment } from '@sketch/gql-types'

export const StyledPill = styled(Pill)`
  margin-left: 8px;
`

interface CurrentPlanProps {
  workspaceId: string
  isWorkspaceOnTrial: boolean
  customerHasPaymentDetails: boolean
  currentPlan?: BillingPlanFragment
  trialEndDate: string
  nextBillingCycleDate?: string
  isSubscriptionEnd: boolean
  isNewPricingEnabled: boolean
  onReactivateSubscription: () => void
}

const Header: React.FC = () => (
  <BillingInfoTable.HeadingCell>Current Plan</BillingInfoTable.HeadingCell>
)

export const CurrentPlan = ({
  workspaceId,
  isWorkspaceOnTrial,
  customerHasPaymentDetails,
  currentPlan,
  trialEndDate,
  isSubscriptionEnd,
  isNewPricingEnabled,
  nextBillingCycleDate,
  onReactivateSubscription,
}: CurrentPlanProps) => {
  /**
   * The workspace is on trial and the customer already configured a following
   * plan
   */
  if (isWorkspaceOnTrial && currentPlan && customerHasPaymentDetails) {
    return (
      <BillingInfoTable.Row>
        <Header />

        <BillingInfoTable.Cell>
          <div>
            <strong>Trial</strong>
            <br />
            Ends on {dateFormat(new Date(trialEndDate))}
          </div>
        </BillingInfoTable.Cell>

        {isSubscriptionEnd && (
          <BillingInfoTable.Cell>
            <Button
              variant="primary"
              size="32"
              onClick={onReactivateSubscription}
            >
              Subscribe Again
            </Button>
          </BillingInfoTable.Cell>
        )}
      </BillingInfoTable.Row>
    )
  } else if (isWorkspaceOnTrial) {
    /**
     * The workspace is only on trial. After that, the workspace will be
     * de-activated
     */
    return (
      <BillingInfoTable.Row>
        <Header />
        <BillingInfoTable.Cell>
          <div>
            <strong>Trial</strong>
            <br />
            Ends on {dateFormat(new Date(trialEndDate))}
          </div>
        </BillingInfoTable.Cell>
        <BillingInfoTable.Cell>
          <LinkButton
            data-testid="setup-subscription-button"
            variant="primary"
            size="32"
            to={routes.WORKSPACE_SUBSCRIBE.create({
              workspaceId,
            })}
          >
            Set Up a Subscription
          </LinkButton>
        </BillingInfoTable.Cell>
      </BillingInfoTable.Row>
    )
  } else {
    /**
     * The workspace is definitely not on trial
     */
    const showPill =
      isNewPricingEnabled &&
      nextBillingCycleDate &&
      Date.parse(nextBillingCycleDate) > nextPricingDate

    return (
      <BillingInfoTable.Row>
        <Header />

        <BillingInfoTable.Cell>
          <div>
            <strong data-testid="plan-name">
              {PLAN_TYPE_LABEL[currentPlan!.type]}
            </strong>
            <br />
            <span data-testid="plan-price-per-editor">
              <strong>{formatPriceShort(currentPlan!.invoicePrice)}</strong> per
              Seat, per {currentPlan?.type === 'YEARLY' ? 'year' : 'month'}
            </span>
            {showPill && (
              <StyledPill variant="secondary">
                {currentPlan?.type === 'YEARLY' ? '$120' : '$12'} in the next
                billing date
              </StyledPill>
            )}
          </div>
        </BillingInfoTable.Cell>
        {isSubscriptionEnd && (
          <BillingInfoTable.Cell>
            <Button
              variant="primary"
              size="32"
              onClick={onReactivateSubscription}
            >
              Subscribe Again
            </Button>
          </BillingInfoTable.Cell>
        )}
      </BillingInfoTable.Row>
    )
  }
}
